// PageFacts — the facts about a Studio page, in one quiet line under its
// title (pen Q76 "Fakta om sidan": facts about the page sit with the page):
// "Page · ✦ AI-suggested · ▤ 1 source". Its kind, the assistant's mark
// (AI-suggested while it is the assistant's plan not kept yet, else AI when
// the assistant wrote it), its state word or the suggestions waiting in it,
// how many parts a course or module holds, and how many sources the project
// has. Only the sources part acts: it opens the Sources page.
//
// Every part is left out when it has nothing to say: no kind (a kind this
// build does not know, or the project itself), no sources (or not loaded
// yet: never "0 sources"), no parts. Nothing at all: no line.
// `pageFactsOf` decides the parts (pure); `PageFacts` words them. Pure.

export type { PageFact, PageFactsInput };
export { PageFacts, pageFactsOf };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Fragment, type ReactNode } from "react";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Icon } from "../Icon/Icon.js";
import type { DocState } from "../StudioDesk/deskFacts.js";
import { useKindWord, useStateWord } from "./Lines.js";
import type { LinkStatus, PageNodeKind } from "./studioPages.js";

type PageFact =
    | { readonly type: "kind"; readonly kind: PageNodeKind }
    // The assistant's plan, not kept yet (the code's Suggested state).
    | { readonly type: "ai_suggested" }
    // The assistant wrote it (ai-transparency: marked, in grey).
    | { readonly type: "ai_made" }
    | { readonly type: "suggestions"; readonly count: number }
    | { readonly type: "state"; readonly state: DocState; readonly ai: boolean }
    | { readonly type: "parts"; readonly count: number }
    | { readonly type: "sources"; readonly count: number };

type PageFactsInput = {
    // Null: no one kind (the project itself, or a kind not known here).
    readonly kind: PageNodeKind | null;
    readonly suggestion: boolean;
    readonly aiAuthored: boolean;
    // A document's status (absent: a course or module page).
    readonly status?: LinkStatus;
    // A course or module page's parts (absent: a document).
    readonly parts?: number;
    // The project's sources (undefined: not known yet).
    readonly sources: number | undefined;
};

const pageFactsOf = (input: PageFactsInput): readonly PageFact[] => {
    const status = ((): readonly PageFact[] => {
        switch (input.status?.type) {
            case undefined:
            case "parts":
                return [];
            case "suggested":
                return [{ type: "suggestions", count: input.status.count }];
            case "doc":
                return [
                    {
                        type: "state",
                        state: input.status.state,
                        ai: input.status.ai,
                    },
                ];
            default:
                return exhaustSwitchCase(input.status);
        }
    })();
    return [
        ...(input.kind === null
            ? []
            : [{ type: "kind", kind: input.kind } satisfies PageFact]),
        ...(input.suggestion
            ? [{ type: "ai_suggested" } satisfies PageFact]
            : input.aiAuthored
              ? [{ type: "ai_made" } satisfies PageFact]
              : []),
        ...status,
        ...(input.parts === undefined || input.parts === 0
            ? []
            : [{ type: "parts", count: input.parts } satisfies PageFact]),
        ...(input.sources === undefined || input.sources === 0
            ? []
            : [{ type: "sources", count: input.sources } satisfies PageFact]),
    ];
};

const PageFacts = ({
    facts,
    onSources,
}: {
    readonly facts: readonly PageFact[];
    // The Sources page opened.
    readonly onSources: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const kindWord = useKindWord();
    const stateWord = useStateWord();
    const worded = (fact: PageFact): ReactNode => {
        switch (fact.type) {
            case "kind":
                return kindWord(fact.kind);
            case "ai_suggested":
                return <AiMarker variant="chip" label={t`AI-suggested`} />;
            case "ai_made":
                return <AiMarker variant="chip" />;
            case "suggestions":
                return t`${fact.count} suggested`;
            case "state":
                return stateWord(fact.state, fact.ai);
            case "parts":
                return t`${plural(fact.count, { one: "# part", other: "# parts" })}`;
            case "sources":
                return (
                    <button
                        type="button"
                        className="bilda-pages__facts-sources"
                        onClick={onSources}
                    >
                        <Icon name="file-text" size="xs" />
                        <span>
                            {t`${plural(fact.count, { one: "# source", other: "# sources" })}`}
                        </span>
                    </button>
                );
            default:
                return exhaustSwitchCase(fact);
        }
    };
    const parts = facts
        .map((fact) => ({ key: fact.type, node: worded(fact) }))
        .filter((p) => p.node !== null);
    if (parts.length === 0) return null;
    return (
        <p className="bilda-pages__facts">
            {parts.map((p, i) => (
                <Fragment key={p.key}>
                    {i === 0 ? null : (
                        <span
                            className="bilda-pages__facts-dot"
                            aria-hidden="true"
                        >
                            ·
                        </span>
                    )}
                    {p.node}
                </Fragment>
            ))}
        </p>
    );
};
